import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Package, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');
  
  const [status, setStatus] = useState('checking');
  const [orderData, setOrderData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (orderId) {
      checkOrderStatus();
    } else {
      setStatus('error');
    }
  }, [orderId]);

  const checkOrderStatus = async () => {
    if (attempts >= 15) {
      setStatus('timeout');
      return;
    }

    try {
      // Önce ödeme durumunu kontrol et
      const paymentRes = await axios.get(`${API}/payment/status/${orderId}`);
      
      if (paymentRes.data.status === 'paid') {
        setStatus('success');
        setOrderData({ order_number: orderId });
        return;
      } else if (paymentRes.data.status === 'failed') {
        setStatus('failed');
        return;
      }
      
      // Hala pending ise tekrar kontrol et
      setAttempts(prev => prev + 1);
      setTimeout(checkOrderStatus, 2000);
    } catch (error) {
      // Ödeme kaydı yoksa sipariş kaydına bak
      try {
        const orderRes = await axios.get(`${API}/orders/by-number/${orderId}`);
        if (orderRes.data && orderRes.data.payment_status === 'paid') {
          setStatus('success');
          setOrderData(orderRes.data);
          return;
        }
      } catch (e) {
        // Sipariş de yoksa bekle
      }
      
      setAttempts(prev => prev + 1);
      if (attempts < 15) {
        setTimeout(checkOrderStatus, 2000);
      } else {
        setStatus('timeout');
      }
    }
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="checkout-loading">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-16 w-16 text-orange-500 animate-spin mx-auto mb-6" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Ödeme Kontrol Ediliyor</h2>
            <p className="text-slate-500">Lütfen bekleyin, ödemeniz doğrulanıyor...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="checkout-success">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Ödeme Başarılı!</h2>
            <p className="text-slate-500 mb-6">
              Siparişiniz alındı ve en kısa sürede hazırlanacak.
            </p>
            
            {orderId && (
              <div className="bg-slate-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-slate-500">Sipariş Numarası</p>
                <p className="font-mono font-bold text-slate-900">{orderId}</p>
              </div>
            )}

            <div className="space-y-3">
              <Link to="/orders">
                <Button className="w-full bg-orange-500 hover:bg-orange-600">
                  <Package className="h-5 w-5 mr-2" />
                  Siparişlerimi Görüntüle
                </Button>
              </Link>
              <Link to="/products">
                <Button variant="outline" className="w-full">
                  Alışverişe Devam Et
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="checkout-error">
      <Card className="w-full max-w-md mx-4">
        <CardContent className="p-8 text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">⚠️</span>
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">
            {status === 'failed' ? 'Ödeme Başarısız' : 
             status === 'timeout' ? 'Zaman Aşımı' : 'Bir Hata Oluştu'}
          </h2>
          <p className="text-slate-500 mb-6">
            {status === 'failed' ? 'Ödeme işlemi başarısız oldu. Lütfen tekrar deneyin.' :
             status === 'timeout' ? 'Ödeme durumu kontrol edilemedi. Siparişlerinizi kontrol edin.' :
             'Ödeme işlemi sırasında bir hata oluştu.'}
          </p>

          <div className="space-y-3">
            <Link to="/orders">
              <Button className="w-full bg-orange-500 hover:bg-orange-600">
                Siparişlerimi Kontrol Et
              </Button>
            </Link>
            <Link to="/cart">
              <Button variant="outline" className="w-full">
                Sepete Dön
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CheckoutSuccessPage;
